import React from "react";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import { updateClass } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";

interface QuickEditClassCapacityDialogProps {
  open: boolean;
  classId: string | null;
  className?: string;
  currentCapacity?: number;
  onClose: () => void;
  onUpdated: () => void;
}

/**
 * A capacity bump right from the booking screen — the full Edit Class form
 * (name, coach, price, ...) is overkill when all you need is a couple more
 * spots. Sends a true partial PATCH with just `capacity`.
 *
 * Capacity lives only on the class definition, not per session, so a change
 * here applies immediately to every session of this class — past, present
 * and future, no per-session override. Lowering it below a session's current
 * booked count doesn't remove anyone already in; it just blocks new bookings
 * on that session until the count drops back under the new cap.
 */
const QuickEditClassCapacityDialog: React.FC<QuickEditClassCapacityDialogProps> = ({
  open,
  classId,
  className,
  currentCapacity,
  onClose,
  onUpdated,
}) => {
  const [capacity, setCapacity] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) setCapacity(currentCapacity !== undefined ? String(currentCapacity) : "");
  }, [open, currentCapacity]);

  const handleSave = async () => {
    if (!classId) return;
    const next = Number(capacity);
    if (!Number.isInteger(next) || next < 1) {
      toast.error("Capacity must be a whole number of at least 1.");
      return;
    }

    setSubmitting(true);
    try {
      await updateClass(classId, { capacity: next });
      toast.success("Capacity updated — applies to every session of this class.");
      onUpdated();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update capacity."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title={`Edit Capacity — ${className || "Class"}`}
      actions={
        <>
          <CustomButton
            label="Cancel"
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
          />
          <CustomButton
            label="Save"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            loading={submitting}
            disabled={submitting || !capacity}
            onClick={handleSave}
          />
        </>
      }
    >
      <Typography sx={{ fontSize: 13, color: "#777", mb: 2 }}>
        Applies to every session of this class immediately, not just this one
        — and doesn't remove anyone already booked if you lower it.
      </Typography>
      <CustomLabel text="Capacity" fontSize={14} fontWeight={500} highlightLabelStar />
      <CustomTextField
        type="number"
        value={capacity}
        onChange={(e: any) => setCapacity(e.target.value)}
        fullWidth
      />
    </CustomDialog>
  );
};

export default QuickEditClassCapacityDialog;
